/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { blackText, secondary, straightFont, white } from '../../../Constants';
import GenericDialog from '../../GenericDialog';
import { useAuth } from '../../../contexts/AuthContext';

// Shared text field styling
const textFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontFamily: straightFont,
    '& fieldset': {
      borderColor: alpha(blackText, 0.2),
    },
    '&:hover fieldset': {
      borderColor: alpha(blackText, 0.4),
    },
    '&.Mui-focused fieldset': {
      borderColor: secondary,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: straightFont,
    color: alpha(blackText, 0.6),
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: secondary,
  },
};

interface ILoginProps {
  showLogin: boolean;
  setShowLogin: Dispatch<SetStateAction<boolean>>;
  setShowSignup: Dispatch<SetStateAction<boolean>>;
  setShowForgotPassword: Dispatch<SetStateAction<boolean>>;
  triggerFavMessage: boolean;
  setTriggerFavMessage: Dispatch<SetStateAction<boolean>>;
  triggerCommentMessage: boolean;
  setTriggerCommentMessage: Dispatch<SetStateAction<boolean>>;
}

export default function LoginModal(props: ILoginProps) {
  const {
    showLogin,
    setShowLogin,
    setShowSignup,
    setShowForgotPassword,
    triggerFavMessage,
    setTriggerFavMessage,
    triggerCommentMessage,
    setTriggerCommentMessage,
  } = props;
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { login } = useAuth();

  useEffect(() => {
    if (showLogin) {
      setError('');
    }

    if (triggerFavMessage) {
      const timer = setTimeout(() => {
        setTriggerFavMessage(false);
      }, 3000);
      clearTimeout(timer);
    }

    if (triggerCommentMessage) {
      const timer = setTimeout(() => {
        setTriggerCommentMessage(false);
      }, 3000);
      clearTimeout(timer);
    }
  }, [showLogin, triggerFavMessage, triggerCommentMessage, setTriggerFavMessage, setTriggerCommentMessage]);

  const handleForgotPassword = () => {
    setShowLogin(false);
    setShowForgotPassword(true);
  };

  const closeModal = () => {
    setShowLogin(false);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      setShowLogin(false);
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleSignup = () => {
    setShowLogin(false);
    setShowSignup(true);
  };

  return (
    <GenericDialog open={showLogin} closeModal={closeModal} isLoading={loading}>
      <Box
        component="form"
        sx={{
          p: 4,
          maxWidth: 400,
          backgroundColor: white,
        }}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Header */}
        <Typography
          variant="h5"
          sx={{
            fontFamily: straightFont,
            fontWeight: 600,
            color: blackText,
            textAlign: 'center',
            mb: 3,
          }}
        >
          Welcome Back
        </Typography>

        {/* Form Fields */}
        <Stack spacing={2.5}>
          <TextField
            type="email"
            label="Email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={textFieldSx}
            variant="outlined"
            size="medium"
            fullWidth
          />
          <TextField
            type="password"
            name="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={textFieldSx}
            variant="outlined"
            size="medium"
            fullWidth
          />
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={closeModal}
            fullWidth
            sx={{
              color: alpha(blackText, 0.7),
              borderColor: alpha(blackText, 0.2),
              fontFamily: straightFont,
              fontWeight: 500,
              py: 1.25,
              textTransform: 'none',
              '&:hover': {
                borderColor: alpha(blackText, 0.4),
                backgroundColor: alpha(blackText, 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            fullWidth
            sx={{
              backgroundColor: secondary,
              fontFamily: straightFont,
              fontWeight: 600,
              py: 1.25,
              textTransform: 'none',
              boxShadow: `0 4px 14px ${alpha(secondary, 0.35)}`,
              '&:hover': {
                backgroundColor: alpha(secondary, 0.9),
                boxShadow: `0 6px 20px ${alpha(secondary, 0.45)}`,
              },
            }}
          >
            Login
          </Button>
        </Stack>

        {/* Footer Links */}
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(blackText, 0.1)}` }}
        >
          <Button
            variant="text"
            onClick={handleSignup}
            sx={{
              fontFamily: straightFont,
              fontSize: '0.875rem',
              color: secondary,
              textTransform: 'none',
              '&:hover': { backgroundColor: alpha(secondary, 0.08) },
            }}
          >
            Create account
          </Button>
          <Typography sx={{ color: alpha(blackText, 0.3) }}>|</Typography>
          <Button
            variant="text"
            onClick={handleForgotPassword}
            sx={{
              fontFamily: straightFont,
              fontSize: '0.875rem',
              color: alpha(blackText, 0.6),
              textTransform: 'none',
              '&:hover': { backgroundColor: alpha(blackText, 0.04) },
            }}
          >
            Forgot password?
          </Button>
        </Stack>

        {/* Snackbars */}
        <Snackbar
          open={triggerFavMessage}
          autoHideDuration={3000}
          onClose={() => setTriggerFavMessage(false)}
          action={
            <IconButton size="small" color="inherit" onClick={() => setTriggerFavMessage(false)}>
              <CloseIcon />
            </IconButton>
          }
        >
          <Alert onClose={() => setTriggerFavMessage(false)} severity="info">
            Log in to save your favorite recipes!
          </Alert>
        </Snackbar>
        <Snackbar
          open={triggerCommentMessage}
          autoHideDuration={3000}
          onClose={() => setTriggerCommentMessage(false)}
          action={
            <IconButton size="small" color="inherit" onClick={() => setTriggerCommentMessage(false)}>
              <CloseIcon />
            </IconButton>
          }
        >
          <Alert onClose={() => setTriggerCommentMessage(false)} severity="info">
            Log in to leave a comment!
          </Alert>
        </Snackbar>
      </Box>
    </GenericDialog>
  );
}
